import { Component, type ReactNode } from 'react'

export type CallsPanel = 'email' | 'text' | 'notes' | 'visibility' | 'record' | 'activity'

type Props = {
  panel: CallsPanel
  resetKey: string
  active?: boolean
  children: ReactNode
}

type State = {
  panel: CallsPanel
  resetKey: string
  failed: boolean
  retryUsed: boolean
}

const panelLabels: Record<CallsPanel, string> = {
  email: 'Email',
  text: 'Text',
  notes: 'Notes',
  visibility: 'Visibility',
  record: 'Record',
  activity: 'Activity',
}

/** Keep render failures inside the selected panel, below the shared phone providers. */
export class CallsPanelBoundary extends Component<Props, State> {
  state: State = {
    panel: this.props.panel,
    resetKey: this.props.resetKey,
    failed: false,
    retryUsed: false,
  }

  static getDerivedStateFromProps(props: Props, state: State): Partial<State> | null {
    if (props.panel === state.panel && props.resetKey === state.resetKey) return null
    return { panel: props.panel, resetKey: props.resetKey, failed: false, retryUsed: false }
  }

  static getDerivedStateFromError(): Partial<State> {
    // Do not retain or report thrown messages, stacks, record identifiers or content.
    return { failed: true }
  }

  private retry = () => {
    this.setState(state => state.failed && !state.retryUsed
      ? { failed: false, retryUsed: true }
      : null)
  }

  render() {
    if (this.props.active === false) return null
    if (!this.state.failed) return this.props.children
    const label = panelLabels[this.props.panel]
    return <section className="runtime-banner degraded" role="alert" data-error-code="CALLS_PANEL_RENDER_FAILED">
      <div>
        <strong>{label} panel is unavailable.</strong>
        <span>Other tabs and call controls remain available. This panel could not be displayed.</span>
        {this.state.retryUsed
          ? <span>The panel still could not be displayed after retrying. Switch to another tab or record to continue.</span>
          : <button className="secondary-btn" type="button" onClick={this.retry}>Retry {label} panel once</button>}
      </div>
    </section>
  }
}
